import React, {ChangeEvent, useMemo, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBinoculars,
    faCalendarDays,
    faChevronLeft,
    faCrow,
    faLocationDot,
    faMapLocationDot,
    faSkullCrossbones
} from "@fortawesome/free-solid-svg-icons";
import {EBirdChecklistByFamily, EBirdChecklistBySpecies, EBirdObservationsBySpecies} from "ebird-mydata-reader";
import {ChecklistCard} from "../components/checklist-card";
import {formatDate, getFamilySlug} from "../helpers/helpers";
import {getRandomDashboardHeroImage} from "../helpers/dashboardHeroImages";
import {toRootRelativeImagePath} from "../helpers/imagePaths";
import './checklist.scss';
import {useLocation, useNavigate} from "react-router-dom";

export type ChecklistProps = {
    checklist: EBirdChecklistByFamily[],
    observationsBySpecies: EBirdObservationsBySpecies[]
}

type ChecklistFilter = 'all'|'seen'|'unseen'|'extinct';
type ChecklistSpeciesImage = EBirdChecklistBySpecies['image'] & {
    sourceUrl?: string | null;
}

const getFamilyStats = (family: EBirdChecklistByFamily) => {
    const extantSpecies = family.speciesList.filter(species => !species.isExtinct);
    const seenSpecies = extantSpecies.filter(species => species.seen).length;
    const totalSpecies = extantSpecies.length;
    const extinctSpecies = family.speciesList.length - totalSpecies;

    return {
        seenSpecies,
        totalSpecies,
        unseenSpecies: Math.max(totalSpecies - seenSpecies, 0),
        extinctSpecies,
        isExtinct: totalSpecies === 0 && extinctSpecies > 0,
        seenPercent: Math.round(totalSpecies ? (seenSpecies / totalSpecies) * 100 : 0)
    };
}

const getImageAttributionUrl = (image: EBirdChecklistBySpecies['image']) => {
    return (image as ChecklistSpeciesImage | undefined)?.sourceUrl ?? undefined;
}

const getFamilyScientificName = (family: EBirdChecklistByFamily) => {
    return family.familyScientific || family.familyName;
}

const getFamilyCommonName = (family: EBirdChecklistByFamily) => {
    return family.familyCommon || family.familyName;
}

export default function Checklist({checklist, observationsBySpecies}: ChecklistProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState<ChecklistFilter>('all');
    const [activeSpeciesFilter, setActiveSpeciesFilter] = useState<ChecklistFilter>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [speciesSearchTerm, setSpeciesSearchTerm] = useState('');
    const familySlug = location.pathname.startsWith('/checklist/family/')
        ? decodeURIComponent(location.pathname.replace('/checklist/family/', '').split('/')[0])
        : undefined;
    const selectedFamily = useMemo(() => {
        if (!familySlug) return null;
        return checklist.find(family => getFamilySlug(family.familyName) === familySlug) ?? null;
    }, [checklist, familySlug]);
    const checklistHeroImage = useMemo(() => {
        return getRandomDashboardHeroImage();
    }, []);
    const selectedFamilyHeroImage = useMemo(() => {
        if (!selectedFamily) return undefined;

        const familyImage = selectedFamily.speciesList[0]?.image;
        return toRootRelativeImagePath(familyImage?.medium ?? familyImage?.thumb);
    }, [selectedFamily]);
    const heroImage = selectedFamilyHeroImage ?? checklistHeroImage;
    const selectedFamilyStats = useMemo(() => {
        return selectedFamily ? getFamilyStats(selectedFamily) : null;
    }, [selectedFamily]);
    const observationCountBySpecies = useMemo(() => {
        const counts = new Map<number, number>();

        for (const family of checklist) {
            for (const checklistSpecies of family.speciesList) {
                const taxonomicOrders = new Set<number>([
                    checklistSpecies.taxonomicOrder,
                    ...(checklistSpecies.alternateTaxonomicOrders ?? [])
                ]);
                const count = observationsBySpecies
                    .filter(species => taxonomicOrders.has(species.taxonomicOrder))
                    .reduce((total, species) => total + species.observations.length, 0);

                counts.set(checklistSpecies.taxonomicOrder, count);
            }
        }

        return counts;
    }, [checklist, observationsBySpecies]);

    const checklistStats = useMemo(() => {
        const familyStats = checklist.map(getFamilyStats);
        const seenSpecies = familyStats.reduce((total, stats) => total + stats.seenSpecies, 0);
        const totalSpecies = familyStats.reduce((total, stats) => total + stats.totalSpecies, 0);
        const years = new Set<number>();

        for (const family of checklist) {
            for (const species of family.speciesList) {
                if (!species.seen || !species.firstObservation?.date) continue;

                const year = parseInt(species.firstObservation.date.split('-')[0]);
                if (!Number.isNaN(year)) {
                    years.add(year);
                }
            }
        }

        return {
            seenSpecies,
            totalSpecies,
            unseenSpecies: Math.max(totalSpecies - seenSpecies, 0),
            seenFamilies: checklist.filter(family => {
                const stats = getFamilyStats(family);
                return !stats.isExtinct && family.seen;
            }).length,
            unseenFamilies: checklist.filter(family => {
                const stats = getFamilyStats(family);
                return !stats.isExtinct && !family.seen;
            }).length,
            extinctFamilies: familyStats.filter(stats => stats.isExtinct).length,
            years: years.size
        };
    }, [checklist]);

    const visibleFamilies = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return checklist.filter(family => {
            const familyStats = getFamilyStats(family);

            if (activeFilter === 'seen' && (!family.seen || familyStats.isExtinct)) return false;
            if (activeFilter === 'unseen' && (family.seen || familyStats.isExtinct)) return false;
            if (activeFilter === 'extinct' && !familyStats.isExtinct) return false;
            if (!normalizedSearch) return true;

            return `${family.familyName} ${family.familyScientific} ${family.familyCommon}`.toLowerCase().includes(normalizedSearch);
        });
    }, [activeFilter, checklist, searchTerm]);
    const visibleSpecies = useMemo(() => {
        if (!selectedFamily) return [];

        const normalizedSearch = speciesSearchTerm.trim().toLowerCase();

        return selectedFamily.speciesList.filter(species => {
            if (activeSpeciesFilter === 'seen' && (!species.seen || species.isExtinct)) return false;
            if (activeSpeciesFilter === 'unseen' && (species.seen || species.isExtinct)) return false;
            if (activeSpeciesFilter === 'extinct' && !species.isExtinct) return false;
            if (!normalizedSearch) return true;

            return `${species.commonName} ${species.scientificName}`.toLowerCase().includes(normalizedSearch);
        });
    }, [activeSpeciesFilter, selectedFamily, speciesSearchTerm]);

    const handleChangeFamily = (family: EBirdChecklistByFamily) => {
        setActiveSpeciesFilter('all');
        setSpeciesSearchTerm('');
        navigate(`/checklist/family/${getFamilySlug(family.familyName)}`);
    }

    const handleShowAllFamilies = () => {
        navigate('/checklist');
    }

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    }

    const handleSpeciesSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSpeciesSearchTerm(event.target.value);
    }

    const getSpeciesTitle = (species: EBirdChecklistBySpecies) => {
        return species.commonName;
    }

    const getSpeciesSubtitle = (species: EBirdChecklistBySpecies) => {
        return <>{species.scientificName}</>;
    }

    const getSpeciesDescription = (species: EBirdChecklistBySpecies) => {
        const firstObservation = species.firstObservation;
        const observationCount = observationCountBySpecies.get(species.taxonomicOrder) ?? 0;
        const locationBits = firstObservation
            ? [firstObservation.location, firstObservation.county, firstObservation.stateProvince].filter(Boolean)
            : [];

        return <>
            {species.seen ?
                <>
                    <span className="family-card__detail-row">
                        <FontAwesomeIcon icon={faBinoculars} aria-hidden="true" />
                        <span>Seen {observationCount.toLocaleString()} {observationCount === 1 ? 'time' : 'times'}</span>
                    </span>
                    <span className="family-card__detail-row">
                        <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                        <span><strong className="family-card__detail-heading">First Seen:</strong> {locationBits.join(', ')}</span>
                    </span>
                    <span className="family-card__detail-row">
                        <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                        <span>{formatDate(firstObservation.date)}</span>
                    </span>
                    <button
                        type="button"
                        className="family-card__map-cta"
                        onClick={() => navigate(`/map?year=life&mode=species&species=${species.speciesCode}`)}
                    >
                        Show my sightings on a map
                        <FontAwesomeIcon icon={faMapLocationDot} aria-hidden="true" />
                    </button>
                </> :
                <>
                    <span className="family-card__detail-row">
                        <FontAwesomeIcon icon={faCrow} aria-hidden="true" />
                        <span>Not seen yet</span>
                    </span>
                </>}
        </>;
    }

    return (
        <section
            className="checklist"
            aria-labelledby="checklist-title"
            style={{'--checklist-hero-image': `url("${heroImage}")`} as React.CSSProperties}
        >
            <div className={`checklist__hero ${selectedFamily ? 'checklist__hero--family' : ''}`}>
                <div className="checklist__hero-content">
                    {selectedFamily ?
                        <button type="button" className="checklist__hero-back" onClick={handleShowAllFamilies}>
                            <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                            All Families
                        </button> :
                        null
                    }
                    <h1 id="checklist-title" className="checklist__title">
                        {selectedFamily ? getFamilyScientificName(selectedFamily) : 'Your birding checklist'}
                    </h1>
                    <p className="checklist__intro">
                        {selectedFamily ?
                            getFamilyCommonName(selectedFamily) :
                            'Your life list organized by bird family.'
                        }
                    </p>
                    <div className="checklist__stats" aria-label="Checklist summary">
                        <div className="checklist__stat">
                            <span className="checklist__stat-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faBinoculars} />
                            </span>
                            <span>
                                <strong>{(selectedFamilyStats?.seenSpecies ?? checklistStats.seenSpecies).toLocaleString()}</strong>
                                <span>Species Seen</span>
                            </span>
                        </div>
                        {selectedFamily ?
                            <>
                                <div className="checklist__stat">
                                    <span className="checklist__stat-icon" aria-hidden="true">
                                        <FontAwesomeIcon icon={faCrow} />
                                    </span>
                                    <span>
                                        <strong>{(selectedFamilyStats?.seenPercent ?? 0).toLocaleString()}%</strong>
                                        <span>Species Seen</span>
                                    </span>
                                </div>
                                <div className="checklist__stat">
                                    <span className="checklist__stat-icon" aria-hidden="true">
                                        <FontAwesomeIcon icon={faSkullCrossbones} />
                                    </span>
                                    <span>
                                        <strong>{(selectedFamilyStats?.extinctSpecies ?? 0).toLocaleString()}</strong>
                                        <span>Species Extinct</span>
                                    </span>
                                </div>
                            </> :
                            <>
                                <div className="checklist__stat">
                                    <span className="checklist__stat-icon" aria-hidden="true">
                                        <FontAwesomeIcon icon={faCrow} />
                                    </span>
                                    <span>
                                        <strong>{checklistStats.totalSpecies.toLocaleString()}</strong>
                                        <span>Total Species</span>
                                    </span>
                                </div>
                                <div className="checklist__stat">
                                    <span className="checklist__stat-icon" aria-hidden="true">
                                        <FontAwesomeIcon icon={faCalendarDays} />
                                    </span>
                                    <span>
                                        <strong>{checklistStats.years.toLocaleString()}</strong>
                                        <span>{checklistStats.years === 1 ? 'Year Birding' : 'Years Birding'}</span>
                                    </span>
                                </div>
                            </>
                        }
                    </div>
                </div>
            </div>

            <div className="checklist__panel">
                {selectedFamily === null ?
                    <div className="checklist__toolbar">
                        <div className="checklist__tabs" aria-label="Checklist filters">
                            <button
                                type="button"
                                className={`checklist__tab ${activeFilter === 'all' ? 'is-active' : ''}`}
                                onClick={() => setActiveFilter('all')}
                            >
                                All Families
                            </button>
                            <button
                                type="button"
                                className={`checklist__tab ${activeFilter === 'seen' ? 'is-active' : ''}`}
                                onClick={() => setActiveFilter('seen')}
                            >
                                Seen ({checklistStats.seenFamilies.toLocaleString()})
                            </button>
                            <button
                                type="button"
                                className={`checklist__tab ${activeFilter === 'unseen' ? 'is-active' : ''}`}
                                onClick={() => setActiveFilter('unseen')}
                            >
                                Not Seen ({checklistStats.unseenFamilies.toLocaleString()})
                            </button>
                            <button
                                type="button"
                                className={`checklist__tab ${activeFilter === 'extinct' ? 'is-active' : ''}`}
                                onClick={() => setActiveFilter('extinct')}
                            >
                                Extinct ({checklistStats.extinctFamilies.toLocaleString()})
                            </button>
                        </div>
                        <label className="checklist__search">
                            <span>Search families</span>
                            <input
                                type="search"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                placeholder="Search families..."
                            />
                        </label>
                    </div> :
                    <div className="checklist__toolbar">
                        <div className="checklist__tabs" aria-label="Species filters">
                            <button
                                type="button"
                                className={`checklist__tab ${activeSpeciesFilter === 'all' ? 'is-active' : ''}`}
                                onClick={() => setActiveSpeciesFilter('all')}
                            >
                                All <span className="checklist__tab-optional">Species </span>({selectedFamily.totalCount.toLocaleString()})
                            </button>
                            <button
                                type="button"
                                className={`checklist__tab ${activeSpeciesFilter === 'seen' ? 'is-active' : ''}`}
                                onClick={() => setActiveSpeciesFilter('seen')}
                            >
                                Seen ({(selectedFamilyStats?.seenSpecies ?? 0).toLocaleString()})
                            </button>
                            <button
                                type="button"
                                className={`checklist__tab ${activeSpeciesFilter === 'unseen' ? 'is-active' : ''}`}
                                onClick={() => setActiveSpeciesFilter('unseen')}
                            >
                                Not Seen ({(selectedFamilyStats?.unseenSpecies ?? 0).toLocaleString()})
                            </button>
                            <button
                                type="button"
                                className={`checklist__tab ${activeSpeciesFilter === 'extinct' ? 'is-active' : ''}`}
                                onClick={() => setActiveSpeciesFilter('extinct')}
                            >
                                Extinct ({(selectedFamilyStats?.extinctSpecies ?? 0).toLocaleString()})
                            </button>
                        </div>
                        <label className="checklist__search">
                            <span>Search species</span>
                            <input
                                type="search"
                                value={speciesSearchTerm}
                                onChange={handleSpeciesSearchChange}
                                placeholder="Search species..."
                            />
                        </label>
                    </div>
                }

                {selectedFamily === null ?
                    visibleFamilies.length ?
                        <ul className="checklist-list">
                            {visibleFamilies.map(family => {
                                const familyStats = getFamilyStats(family);

                                return (
                                    <li className="checklist-list__item" key={family.familyName}>
                                        <ChecklistCard
                                            subject={family}
                                            seen={family.seen}
                                            title={getFamilyScientificName(family)}
                                            alt={getFamilyCommonName(family)}
                                            subtitle={getFamilyCommonName(family)}
                                            statusLabel={familyStats.isExtinct ? 'Extinct' : undefined}
                                            statusVariant={familyStats.isExtinct ? 'extinct' : undefined}
                                            statCount={`${familyStats.seenSpecies} / ${familyStats.totalSpecies} species`}
                                            img={toRootRelativeImagePath(family.speciesList[0]?.image?.medium ?? family.speciesList[0]?.image?.thumb) || '/assets/fallback-bird.svg'}
                                            statProgress={familyStats.totalSpecies ? familyStats.seenSpecies / familyStats.totalSpecies : 0}
                                            onClick={handleChangeFamily}
                                        />
                                    </li>
                                );
                            })}
                        </ul> :
                        <div className="checklist__empty-state">
                            <strong>No families found</strong>
                            <span>Try a different search or switch family filters.</span>
                        </div> :
                    visibleSpecies.length ?
                        <ul className="checklist-list">
                            {visibleSpecies.map(species => (
                                <li className="checklist-list__item" key={species.scientificName}>
                                    <ChecklistCard
                                        subject={species}
                                        seen={species.seen}
                                        title={getSpeciesTitle(species)}
                                        subtitle={getSpeciesSubtitle(species)}
                                        description={getSpeciesDescription(species)}
                                        statusLabel={species.isExtinct ? 'Extinct' : undefined}
                                        statusVariant={species.isExtinct ? 'extinct' : undefined}
                                        img={toRootRelativeImagePath(species.image?.medium ?? species.image?.thumb) || '/assets/fallback-bird.svg'}
                                        attributionUrl={getImageAttributionUrl(species.image)}
                                        alt={species.commonName}
                                    />
                                </li>
                            ))}
                        </ul> :
                        <div className="checklist__empty-state">
                            <strong>No species found</strong>
                            <span>Try a different search or switch species filters.</span>
                        </div>
                }

            </div>
        </section>
    );
}
